// Strong-password policy — mirrors /app/backend/core.py validate_password_strength.
// Keep these two files in lockstep. Backend remains source of truth; this is
// just for instant client-side feedback so users don't waste a round-trip on
// a too-weak password.

export const PASSWORD_MIN_LENGTH = 14;
export const PASSWORD_MAX_LENGTH = 128;

// Same definition as the backend's `[^A-Za-z0-9\s]`. Treats unicode
// punctuation as a symbol too.
const SYMBOL_RE = /[^A-Za-z0-9\s]/;

/**
 * Returns null if the password satisfies the policy, otherwise a
 * single human-readable error message identical to what the backend
 * would return on the same input.
 */
export function validatePassword(pw) {
  if (!pw) return "Password is required";
  if (pw.length < PASSWORD_MIN_LENGTH) return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  if (pw.length > PASSWORD_MAX_LENGTH) return `Password must be ${PASSWORD_MAX_LENGTH} characters or fewer`;
  if (!/[a-z]/.test(pw)) return "Password must include at least one lowercase letter";
  if (!/[A-Z]/.test(pw)) return "Password must include at least one uppercase letter";
  if (!/[0-9]/.test(pw)) return "Password must include at least one number";
  if (!SYMBOL_RE.test(pw)) return "Password must include at least one symbol (e.g. ! @ # $ % & * ?)";
  return null;
}

/**
 * Returns an object describing which rules pass/fail. Used for the
 * inline checklist UI under password inputs so users see exactly what's
 * missing as they type.
 */
export function passwordChecklist(pw) {
  const s = pw || "";
  return {
    length: s.length >= PASSWORD_MIN_LENGTH,
    lower:  /[a-z]/.test(s),
    upper:  /[A-Z]/.test(s),
    digit:  /[0-9]/.test(s),
    symbol: SYMBOL_RE.test(s),
  };
}

/**
 * Generate a 18-char strong password that is guaranteed to satisfy every
 * rule, with no ambiguous characters (no 0/O/1/l/I) so the user can
 * actually retype it from a sticky note if they have to.
 *
 * Uses crypto.getRandomValues — never Math.random. Each character is
 * picked from a category-specific alphabet to guarantee category
 * coverage, then the whole string is shuffled so the categories aren't
 * positionally predictable.
 */
const LOWER  = "abcdefghijkmnpqrstuvwxyz";          // no l
const UPPER  = "ABCDEFGHJKLMNPQRSTUVWXYZ";          // no I, O
const DIGITS = "23456789";                          // no 0, 1
const SYMBOLS = "!@#$%&*?+-=";                      // shell- and url-safe set

function _pick(alphabet, n) {
  const out = [];
  const buf = new Uint32Array(n);
  window.crypto.getRandomValues(buf);
  for (let i = 0; i < n; i++) {
    out.push(alphabet[buf[i] % alphabet.length]);
  }
  return out;
}

function _shuffle(arr) {
  const buf = new Uint32Array(arr.length);
  window.crypto.getRandomValues(buf);
  // Fisher–Yates using the cryptographically random index source.
  for (let i = arr.length - 1; i > 0; i--) {
    const j = buf[i] % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function generateStrongPassword(length = 18) {
  // Guarantee at least one of each required class, then fill the rest.
  const ALL = LOWER + UPPER + DIGITS + SYMBOLS;
  const chars = [
    ..._pick(LOWER,  2),
    ..._pick(UPPER,  2),
    ..._pick(DIGITS, 2),
    ..._pick(SYMBOLS, 2),
    ..._pick(ALL,    Math.max(0, length - 8)),
  ];
  return _shuffle(chars).join("");
}
